import debounce from 'lodash/debounce';
import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Input, SxProps, Theme } from '@mui/material';
import { IConditionWithRange } from '@tymely/atoms';
import { CONDITION_EDITOR_DEBOUNCE_INTERVAL_MS } from '@tymely/config';

import { BaseConditionEditor, ConditionEditorProps } from './BaseConditionEditor';

export const RangeConditionEditor = memo((props: ConditionEditorProps<IConditionWithRange>) => {
    const [argValue, setArgValue] = useState(props.condition.value?.value);

    useEffect(() => {
        setArgValue(props.condition.value?.value);
    }, [props.condition.value?.value]);

    const setCondition = useMemo(
        () =>
            debounce((value: typeof props.condition.value.value) => {
                if (value?.upper_bound !== undefined || value?.lower_bound !== undefined) {
                    props.setCondition({
                        argument_metadata: props.argumentMetadata,
                        argument_metadata_id: props.argumentMetadata.id,
                        ...props.condition,
                        value: { ...props.condition.value, value },
                    });
                } else {
                    props.setCondition({
                        argument_metadata: props.argumentMetadata,
                        argument_metadata_id: props.argumentMetadata.id,
                        ...props.condition,
                        value: { ...props.condition.value, value: null },
                    });
                }
            }, CONDITION_EDITOR_DEBOUNCE_INTERVAL_MS),
        [props.condition],
    );

    const handleChangeValue = useCallback(
        (value: typeof props.condition.value.value) => {
            setArgValue(value);
            setCondition(value);
        },
        [setCondition],
    );

    const setLowerBound = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const lowerBound = event.target.value;
            handleChangeValue({
                upper_bound: argValue?.upper_bound,
                lower_bound: Number(lowerBound) ? Number(lowerBound) : undefined,
            });
        },
        [argValue, handleChangeValue],
    );

    const setUpperBound = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const upperBound = event.target.value;
            handleChangeValue({
                upper_bound: Number(upperBound) ? Number(upperBound) : undefined,
                lower_bound: argValue?.lower_bound,
            });
        },
        [argValue, handleChangeValue],
    );

    const onFocus = useCallback(() => {
        props.onInputFocusChange?.(true);
    }, [props.onInputFocusChange]);

    const sharedInputSx: SxProps<Theme> = {
        px: 1,
        py: 1,
        width: `clamp(4ch, ${Math.max(4, argValue?.upper_bound?.toString().length ?? 0)}ch, 40ch)`,
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: props.disabled ? 'transparent' : (theme) => theme.palette.grey[200],
        },
        '&:focus-within': {
            backgroundColor: props.disabled ? 'transparent' : (theme) => theme.palette.grey[200],
        },
    };

    return (
        <BaseConditionEditor {...props}>
            <Input
                value={argValue?.lower_bound ?? ''}
                onChange={setLowerBound}
                disabled={props.disabled}
                disableUnderline={props.disableUnderline ?? true}
                error={false}
                sx={{
                    ...sharedInputSx,
                    mr: 0.5,
                }}
                inputProps={{ sx: { p: 0 } }}
                onFocus={onFocus}
            />
            <Input
                value={argValue?.upper_bound ?? ''}
                onChange={setUpperBound}
                disabled={props.disabled}
                disableUnderline={props.disableUnderline ?? true}
                error={false}
                sx={sharedInputSx}
                inputProps={{ sx: { p: 0 } }}
                onFocus={onFocus}
            />
        </BaseConditionEditor>
    );
});

RangeConditionEditor.displayName = 'RangeConditionEditor';
